import { NextPage } from "next";
import Image from "next/image";
import { useIntl } from "react-intl";
import { PageProps } from "@/pages/_app";
import Layout from "@/components/layout/Layout";
import { SEO } from "@/interfaces/seo";
import { SEOWithoutTemplate } from "@/interfaces/seoWithoutTemplate";
import HeroBlock, {
  HeroBlockProps,
} from "@/components/common/heroBlock/HeroBlock";
import {
  DescriptiveBlockList,
  DescriptiveBlockListProps,
} from "@/components/common/descriptiveBlockList/DescriptiveBlockList";
import { Section } from "@/components/common/section/Section";
import { Article, ArticleProps } from "@/components/common/article/Article";
import {
  PointOfInterestList,
  PointOfInterestListProps,
} from "@/components/common/pointOfInterestList/PointOfInterestList";
import {
  DescriptionWithArticleProps,
  FAQProps,
} from "@/utils/formatCategoryPage";
import {
  addBasePathToStrapiMedia,
  formatStrapiEntity,
  StrapiEntity,
  StrapiImage,
} from "@/utils/strapi";
import {
  AgencyCardList,
  AgencyCardListProps,
} from "@/components/pages/vtc/agencyCardList/AgencyCardList";
import { DescriptionWithArticle } from "@/components/common/descriptionWithArticle/DescriptionWithArticle";

import ReviewsBlock2 from "@/components/common/reviewsBlockV1/ReviewsBlock";
import {
  RentACarReview,
  ReviewsConst,
} from "@/components/common/reviewsBlock/ReviewsBlock";
import { StructuredReviewProps } from "@/components/common/structuredData/StructuredData.interface";
import { FAQ } from "../vehicle/categoryPage/faq/FAQ";
import styles from "./GenericPage.module.scss";

const ReviewsBlock = ({
  name,
  agency,
  rentACarReview,
  reviewsConst,
  image,
}: StructuredReviewProps) => {
  return (
    <ReviewsBlock2
      name={name}
      agency={agency}
      rentACarReview={rentACarReview}
      reviewsConst={reviewsConst}
      image={image}
    />
  );
};

export enum GenericPageType {
  NOS_DOSSIERS = "NOS_DOSSIERS",
  GROUPE = "GROUPE",
  UTILES = "UTILES",
}

export type GenericPageContent = {
  heroBlock: HeroBlockProps;
  banner?: StrapiEntity<StrapiImage>;
  presentation?: ArticleProps;
  pointOfInterestList?: PointOfInterestListProps;
  descriptiveBlockList?: DescriptiveBlockListProps;
  nosServicesDescription?: DescriptionWithArticleProps[] | undefined;
  agencyCardList?: AgencyCardListProps;
  faq?: FAQProps;
  rentACarReview?: RentACarReview;
  reviewsConst?: ReviewsConst;
  breadcrumbLabel: string;
  seo?: SEO;
  seoWithoutTemplate: SEOWithoutTemplate;
};
type GenericPageProps = PageProps &
  GenericPageContent & {
    defaultCanonicalRoute: string;
  };

export const GenericPage: NextPage<GenericPageProps> = ({
  layout,
  heroBlock,
  banner,
  presentation,
  pointOfInterestList,
  descriptiveBlockList,
  nosServicesDescription,
  agencyCardList,
  faq,
  rentACarReview,
  reviewsConst,
  breadcrumbLabel,
  seo,
  seoWithoutTemplate,
  defaultCanonicalRoute,
}: GenericPageProps) => {
  const intl = useIntl();

  return (
    <>
      <Layout
        layout={layout}
        defaultCanonicalRoute={defaultCanonicalRoute}
        title={seoWithoutTemplate.title}
        description={seoWithoutTemplate.description}
        breadcrumbs={[
          { title: intl.formatMessage({ id: "home" }), url: "/" },
          {
            title: breadcrumbLabel,
            url: defaultCanonicalRoute,
          },
        ]}
        pageMeta={seo}
      >
        <HeroBlock {...heroBlock} />
        <Section>
          {banner?.data && (
            <div className={styles.imageContainer}>
              <Image
                src={addBasePathToStrapiMedia(formatStrapiEntity(banner))}
                objectFit="contain"
                objectPosition="center"
                alt={formatStrapiEntity(banner).alternativeText}
                layout="fill"
              />
            </div>
          )}
          {presentation && <Article {...presentation} />}

          {pointOfInterestList && (
            <PointOfInterestList
              pointOfInterestList={pointOfInterestList}
              location={breadcrumbLabel}
            />
          )}
          {nosServicesDescription?.map((cat, index: number) => (
            <DescriptionWithArticle {...cat} key={index} />
          ))}
          <div className={styles.divider}></div>
          {descriptiveBlockList && (
            <DescriptiveBlockList {...descriptiveBlockList} />
          )}
          {agencyCardList && <AgencyCardList {...agencyCardList} />}
        </Section>
        {faq && <FAQ {...faq} />}
        <Section>
          {rentACarReview && (
            <ReviewsBlock
              rentACarReview={rentACarReview}
              reviewsConst={reviewsConst}
            />
          )}
        </Section>
      </Layout>
    </>
  );
};
